import { createContext, useCallback, useContext, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, Children, isValidElement, useRef, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { initials } from "@/lib/format";
import { mediaUrl } from "@/lib/media";
import { Icon } from "./Icon";

export function cx(...a: (string | false | null | undefined)[]) {
  return a.filter(Boolean).join(" ");
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "primary" | "dark" | "light" | "danger" | "ghost"; size?: "sm" | "md" | "lg" };
/** Buttons show an icon for what they do, picked from their words. */
const BUTTON_ICONS: [RegExp, string][] = [
  [/^(add|new)\b/i, "plus"],
  [/^(edit|adjust)\b/i, "pencil"],
  [/^(save|confirm|mark|record)\b/i, "check"],
  [/^(cancel|close)\b/i, "x"],
  [/^(delete|remove|revoke)\b/i, "trash"],
  [/^print\b/i, "printer"],
  [/^(excel|pdf|export|download)\b|\(excel\)$/i, "download"],
  [/^(take payment|refund|send cash|take starting cash)\b/i, "cash"],
  [/^(receive|count and receive|move|transfer)\b/i, "truck"],
  [/^(show|see|find)\b/i, "search"],
  [/^(get a setup|set up)\b/i, "gear"],
  [/^start\b/i, "tag"],
  [/^previous$/i, "left"],
  [/^next$/i, "chev"],
];

export function Button({ tone = "light", size = "md", className, children, ...p }: BtnProps) {
  const words = typeof children === "string" ? children.replace(/^\+\s*/, "").trim() : null;
  const icon = words ? BUTTON_ICONS.find(([r]) => r.test(words))?.[1] : undefined;
  const mark = icon ? <Icon name={icon} size={size === "sm" ? 15 : 17} className="flex-none" /> : null;
  const t = {
    primary: "bg-brand text-white hover:bg-brand-dark border border-brand",
    dark: "bg-ink text-white hover:bg-graphite border border-ink",
    light: "bg-white text-ink border border-line-2 hover:border-ink",
    danger: "bg-white text-stop border border-stop hover:bg-stop hover:text-white",
    ghost: "bg-transparent text-muted hover:text-ink border border-transparent",
  }[tone];
  const s = { sm: "h-10 px-3 text-[13px]", md: `${import.meta.env.VITE_BRAND ? "h-11" : "h-12"} px-4 text-sm`, lg: "h-14 px-5 text-base" }[size];
  return <button type="button" {...p} className={cx("inline-flex items-center justify-center gap-2 font-bold transition-colors disabled:opacity-45", t, s, className)}>{icon && icon !== "chev" ? mark : null}{words ?? children}{icon === "chev" ? mark : null}</button>;
}

export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cx("flex flex-col gap-1.5", className)}>
      <span className="text-[13px] font-semibold">{label}</span>
      {children}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}
export const inputCls = `${import.meta.env.VITE_BRAND ? "h-11" : "h-12"} w-full border border-line-2 bg-white px-3 text-[15px] outline-none focus:border-ink`;
export function Input(p: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={cx(inputCls, p.className)} />;
}
/** A password or PIN box with an eye button, so you can check what you typed. */
export function PasswordInput({ startShown, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { startShown?: boolean }) {
  const [show, setShow] = useState(!!startShown);
  return (
    <span className="relative block">
      <input {...p} type={show ? "text" : "password"} className={cx(inputCls, "pr-12", p.className)} />
      <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide" : "Show"} title={show ? "Hide" : "Show"} aria-pressed={show} className="absolute inset-y-0 right-1.5 my-auto grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-ink">
        <Icon name={show ? "eyeOff" : "eye"} size={19} />
      </button>
    </span>
  );
}
/** The modern dropdown (Buildwise). Takes the same props and <option> children as a plain select. */
type SelectOption = { value: string; label: string; disabled: boolean };
const optionText = (c: ReactNode): string => Children.toArray(c).map((x) => (typeof x === "string" || typeof x === "number" ? String(x) : "")).join("");
function optionsOf(children: ReactNode): SelectOption[] {
  return Children.toArray(children).flatMap((c): SelectOption[] => {
    if (!isValidElement(c)) return [];
    const p = c.props as { value?: string | number; disabled?: boolean; children?: ReactNode };
    if (c.type !== "option") return optionsOf(p.children);
    const label = optionText(p.children);
    return [{ value: p.value == null ? label : String(p.value), label, disabled: !!p.disabled }];
  });
}
function ModernSelect({ value, onChange, disabled, children, className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  const options = optionsOf(children);
  const current = String(value ?? "");
  const chosen = options.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [find, setFind] = useState("");
  const sheet = window.innerWidth < 768;
  const [box, setBox] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const show = () => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom > 300 || r.top < 300;
    const width = Math.max(r.width, 190);
    setBox({ left: Math.min(r.left, window.innerWidth - width - 8), width, ...(below ? { top: r.bottom + 6 } : { bottom: window.innerHeight - r.top + 6 }) });
    setActive(Math.max(0, options.findIndex((o) => o.value === current)));
    setFind("");
    setOpen(true);
  };
  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    setOpen(false);
    button.current?.focus();
    if (o.value !== current) onChange?.({ target: { value: o.value } } as unknown as ChangeEvent<HTMLSelectElement>);
  };
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (!list.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false);
    };
    const moved = (e: Event) => {
      if (!sheet && !list.current?.contains(e.target as Node)) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", away);
    window.addEventListener("scroll", moved, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("scroll", moved, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);
  useEffect(() => {
    if (open) list.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);
  const key = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const step = (by: number) => {
      let n = active;
      for (let i = 0; i < options.length; i++) {
        n = (n + by + options.length) % options.length;
        if (!options[n]!.disabled) break;
      }
      setActive(n);
    };
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (options[active]) pick(options[active]!); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    else if (e.key === "Tab") setOpen(false);
    else if (e.key.length === 1) {
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : "") + e.key.toLowerCase(), at: now };
      const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(typed.current.text));
      if (hit >= 0) setActive(hit);
    }
  };
  return (
    <>
      <button ref={button} type="button" role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-label={p["aria-label"]} disabled={disabled} onClick={() => (open ? setOpen(false) : show())} onKeyDown={key} className={cx(inputCls, "flex items-center justify-between gap-2 text-left font-medium text-ink disabled:bg-slate-100 disabled:text-muted", open && "!border-brand", className)}>
        <span className="min-w-0 truncate">{chosen?.label ?? ""}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cx("flex-none text-muted transition-transform", open && "rotate-180")}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && sheet
        ? createPortal(
            <div className="fixed inset-0 z-[90]" role="presentation">
              <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="absolute inset-0 bg-slate-900/50" />
              <div ref={list} role="listbox" aria-label={p["aria-label"]} className="ui-sheet absolute inset-x-0 bottom-0 flex max-h-[76dvh] flex-col rounded-t-[26px] bg-white px-4 pb-5 pt-2.5">
                <span className="mx-auto mb-3 h-[5px] w-11 flex-none rounded-full bg-slate-300" />
                {p["aria-label"] ? <b className="mb-2 flex-none text-[19px] font-extrabold">{p["aria-label"]}</b> : null}
                {options.length > 6 ? <input aria-label="Search" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search" className="mb-2 h-12 w-full flex-none rounded-[14px] bg-slate-100 px-3.5 text-base outline-none" /> : null}
                <div className="min-h-0 overflow-y-auto">
                  {options.filter((o) => o.label.toLowerCase().includes(find.trim().toLowerCase())).map((o, i) => (
                    <button key={o.value + i} type="button" role="option" aria-selected={o.value === current} disabled={o.disabled} onClick={() => pick(o)} className={cx("flex min-h-14 w-full items-center justify-between gap-3 rounded-[14px] px-3 py-2 text-left text-[15px] font-semibold text-ink disabled:text-muted/60", o.value === current && "bg-brand-tint text-brand-ink")}>
                      <span className="min-w-0">{o.label}</span>
                      {o.value === current ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-none"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : open && box
        ? createPortal(
            <div ref={list} role="listbox" aria-label={p["aria-label"]} style={box} className="fixed z-[90] max-h-[280px] overflow-y-auto rounded-2xl bg-white p-1.5 shadow-[0_0_0_1px_rgba(11,21,23,0.08),0_16px_36px_rgba(11,21,23,0.18)]">
              {options.map((o, i) => (
                <button key={o.value + i} type="button" role="option" aria-selected={o.value === current} disabled={o.disabled} onMouseEnter={() => setActive(i)} onClick={() => pick(o)} className={cx("flex min-h-10 w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left text-sm text-ink disabled:cursor-not-allowed disabled:text-muted/60", i === active && !o.disabled && "bg-slate-100", o.value === current && "bg-brand-tint font-bold text-brand-ink")}>
                  <span className="min-w-0">{o.label}</span>
                  {o.value === current ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-none"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
/** A dropdown where several can be ticked. Stays open until Done or a tap outside. */
export function MultiSelect({ options, value, onChange, placeholder, label, allText }: { options: { value: string; label: string }[]; value: string[]; onChange: (v: string[]) => void; placeholder: string; label: string; allText?: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const names = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  return (
    <div ref={box} className="relative">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)} className={cx(inputCls, "flex items-center justify-between gap-2 text-left font-medium text-ink", open && "!border-brand")}>
        <span className={cx("min-w-0 truncate", !names.length && "text-muted")}>{names.length === options.length && allText ? allText : names.join(", ") || placeholder}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cx("flex-none text-muted transition-transform", open && "rotate-180")}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open ? (
        <div role="listbox" aria-multiselectable="true" aria-label={label} className="absolute inset-x-0 top-full z-30 mt-1.5 min-w-[220px] rounded-2xl bg-white p-1.5 shadow-[0_0_0_1px_rgba(11,21,23,0.08),0_16px_36px_rgba(11,21,23,0.18)]">
          {options.map((o) => {
            const on = value.includes(o.value);
            return (
              <button key={o.value} type="button" role="option" aria-selected={on} onClick={() => onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])} className={cx("flex min-h-11 w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left text-sm", on ? "bg-brand-tint font-bold text-brand-ink" : "hover:bg-slate-100")}>
                {o.label}
                {on ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-none"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : null}
              </button>
            );
          })}
          <button type="button" onClick={() => setOpen(false)} className="mt-1 h-11 w-full rounded-[10px] bg-brand text-sm font-bold text-white">{value.length ? `Done (${value.length})` : "Done"}</button>
        </div>
      ) : null}
    </div>
  );
}

export function Select(p: SelectHTMLAttributes<HTMLSelectElement>) {
  if (import.meta.env.VITE_BRAND) return <ModernSelect {...p} />;
  return <select {...p} className={cx(inputCls, "pr-8", p.className)} />;
}

export function Modal({ title, sub, onClose, children, footer, wide, xl }: { title: ReactNode; sub?: ReactNode; onClose?: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; xl?: boolean }) {
  useEffect(() => {
    if (!onClose) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/55 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div role="dialog" aria-modal="true" className={cx("ui-modal flex max-h-[92vh] w-full flex-col bg-white shadow-[0_30px_80px_rgba(0,0,0,0.35)]", xl ? "max-w-6xl" : wide ? "max-w-3xl" : "max-w-lg")}>
        <div className="ui-modal-head flex items-start gap-3 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold">{title}</h2>
            {sub ? <p className="ui-modal-sub mt-0.5 text-[13px]">{sub}</p> : null}
          </div>
          {onClose ? (
            <button aria-label="Close" onClick={onClose} className="ui-close grid h-11 w-11 flex-none place-items-center text-xl">
              ×
            </button>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">{children}</div>
        {footer ? <div className="ui-modal-foot flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Keypad({ onKey, keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "⌫"], dark, height = "h-16" }: { onKey: (k: string) => void; keys?: string[]; dark?: boolean; height?: string }) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {keys.map((k) => {
        const digit = /^[\d.]$/.test(k);
        return (
          <button
            key={k}
            type="button"
            aria-label={k === "⌫" ? "Delete" : k === "." ? "Point" : k}
            onClick={() => onKey(k)}
            className={cx(height, digit ? "font-mono text-2xl font-semibold" : "text-base font-bold", dark ? (digit ? "bg-steel text-white hover:bg-steel-2" : "bg-graphite text-white hover:bg-steel") : "border border-line bg-white hover:border-ink")}
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

/** PIN entry boxes: filled when typed, the next one outlined. */
export function PinDots({ length, dark, size = "h-16" }: { length: number; dark?: boolean; size?: string }) {
  return (
    <div className="grid grid-cols-4 gap-2" aria-live="polite" aria-label={`${length} of 4 digits`}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={cx(size, "ui-pinbox grid place-items-center border-2", dark ? "bg-graphite" : "bg-white", i === length ? (dark ? "border-brand-light" : "border-ink") : dark ? "border-steel-2" : "border-line")}>
          <span className={cx("ui-dot h-4 w-4", i < length ? (dark ? "bg-brand-light" : "bg-brand") : "bg-transparent")} />
        </span>
      ))}
    </div>
  );
}

/** Black or white text, whichever reads better on `hex`. */
export function inkOn(hex: string | undefined): string {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex ?? "");
  if (!m) return "#101214";
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x!, 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return lum > 0.35 ? "#101214" : "#ffffff";
}

/** Apply a keypad key to a string amount ("12.5"). */
export function typeKey(v: string, k: string): string {
  if (k === "⌫") return v.slice(0, -1);
  if (k === "C" || k === "Clear") return "";
  if (k === ".") return v.includes(".") ? v : (v || "0") + ".";
  if (/^\d{1,9}(\.\d{0,1})?$/.test(v) || v === "") return v === "0" ? k : v + k;
  return v;
}

export function Avatar({ name, color, size = 32, image }: { name: string; color?: string; size?: number; image?: string | null }) {
  if (image) return <img src={mediaUrl(image, size * 2)} alt="" style={{ width: size, height: size }} className="ui-avatar object-cover" />;
  return (
    <span style={{ width: size, height: size, background: color ?? "#ffb36b", color: inkOn(color ?? "#ffb36b"), fontSize: Math.max(10, size / 2.8) }} className="ui-avatar grid flex-none place-items-center font-black">
      {initials(name)}
    </span>
  );
}

export function Thumb({ src, name, className }: { src: string | null; name: string; className?: string }) {
  const [bad, setBad] = useState(false);
  return src && !bad ? (
    <img src={mediaUrl(src, 240)} alt="" loading="lazy" onError={() => setBad(true)} className={cx("ui-thumb object-cover", className)} />
  ) : (
    <div title={name} className={cx("ui-thumb grid place-items-center overflow-hidden bg-[#ebe9e3] text-center text-[12px] font-extrabold text-muted", className)}>{initials(name)}</div>
  );
}

export function Badge({ tone = "slate", children }: { tone?: "slate" | "brand" | "amber" | "red" | "blue" | "dark" | "violet"; children: ReactNode }) {
  const t = {
    slate: "bg-[#ebe9e3] text-[#3a4048]",
    brand: "bg-brand-tint text-brand-ink",
    amber: "bg-warn-tint text-warn-ink",
    red: "bg-stop text-white",
    blue: "bg-blue-100 text-blue-900",
    dark: "bg-ink text-white",
    violet: "bg-violet-100 text-violet-900",
  }[tone];
  return <span className={cx("ui-pill inline-flex items-center px-2 py-0.5 text-[11px] font-bold", t)}>{children}</span>;
}

/** One page of a list held in the browser. Goes back to page 1 when the list changes length. */
export function usePaged<T>(rows: T[], size = 15) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows.length]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const cur = Math.min(page, pages);
  return { rows: rows.slice((cur - 1) * size, cur * size), page: cur, size, total: rows.length, setPage };
}

/** Which page numbers to show: the first, the last and those around the current one. */
function pageNumbers(page: number, pages: number): (number | "gap")[] {
  const wanted = [...new Set([1, page - 1, page, page + 1, pages])].filter((p) => p >= 1 && p <= pages).sort((x, y) => x - y);
  return wanted.flatMap((p, i) => (i && p - wanted[i - 1]! > 1 ? (["gap", p] as const) : [p]));
}

export function Pager({ page, size, total, onPage }: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (total <= size) return null;
  const arrow = "grid h-9 w-9 place-items-center rounded-[10px] text-slate-600 hover:bg-slate-100 disabled:opacity-35 disabled:hover:bg-transparent";
  return (
    <nav aria-label="Pages" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-2.5 text-[13px] text-slate-500 print:hidden">
      <span><b className="font-semibold text-ink">{((page - 1) * size + 1).toLocaleString("en-GB")}-{Math.min(page * size, total).toLocaleString("en-GB")}</b> of {total.toLocaleString("en-GB")}</span>
      <span className="flex items-center gap-1">
        <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} className={arrow}><Icon name="left" size={16} /></button>
        {pageNumbers(page, pages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap${i}`} className="w-6 text-center">…</span>
          ) : (
            <button key={p} type="button" aria-label={`Page ${p}`} aria-current={p === page ? "page" : undefined} onClick={() => onPage(p)} className={cx("h-9 min-w-9 rounded-[10px] px-2 font-semibold", p === page ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100")}>{p.toLocaleString("en-GB")}</button>
          ),
        )}
        <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)} className={arrow}><Icon name="chev" size={16} /></button>
      </span>
    </nav>
  );
}

export function Spinner() {
  return <span className="ui-dot inline-block h-4 w-4 animate-spin border-2 border-slate-300 border-t-slate-900" />;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <b className="text-base">{title}</b>
      {children ? <div className="text-sm text-slate-500">{children}</div> : null}
    </div>
  );
}

// ─────────────── toasts ───────────────
type Toast = { id: number; text: string; tone: "ok" | "err" | "info" };
const ToastCtx = createContext<{ push: (text: string, tone?: Toast["tone"]) => void }>({ push: () => {} });
export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "ok") => {
    const id = Date.now() + Math.random();
    setList((l) => [...l, { id, text, tone }].slice(-3));
    setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), tone === "err" ? 6000 : 3200);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-[min(520px,92vw)] -translate-x-1/2 flex-col gap-2">
        {list.map((t) => (
          <div key={t.id} role="status" className={cx("ui-toast px-4 py-3 text-sm font-semibold shadow-lg", t.tone === "err" ? "bg-stop text-white" : t.tone === "info" ? "bg-ink text-white" : "bg-brand text-white")}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx).push;

// ─────────────────────────── confirm dialog (instead of window.confirm) ───────────────────────────
type ConfirmReq = { title: string; body?: ReactNode; ok: string; cancel?: string; danger?: boolean };
const ConfirmCtx = createContext<(r: ConfirmReq) => Promise<boolean>>(async () => false);

/** `const confirm = useConfirm(); if (await confirm({ title, ok })) …` — an in-app dialog that keeps kiosk mode. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<(ConfirmReq & { done: (v: boolean) => void }) | null>(null);
  const ask = useCallback((r: ConfirmReq) => new Promise<boolean>((done) => setReq({ ...r, done })), []);
  const close = (v: boolean) => {
    req?.done(v);
    setReq(null);
  };
  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      {req ? (
        <Modal
          title={req.title}
          onClose={() => close(false)}
          footer={
            <div className="grid w-full grid-cols-2 gap-2">
              <Button size="lg" onClick={() => close(false)}>
                {req.cancel ?? "Cancel"}
              </Button>
              <Button size="lg" tone={req.danger ? "danger" : "primary"} onClick={() => close(true)} className={req.danger ? "!bg-stop !text-white" : ""} autoFocus>
                {req.ok}
              </Button>
            </div>
          }
        >
          {req.body ? <div className="text-[15px] leading-relaxed text-[#3a4048]">{req.body}</div> : null}
        </Modal>
      ) : null}
    </ConfirmCtx.Provider>
  );
}
export const useConfirm = () => useContext(ConfirmCtx);
